import {
  concordanciaFotoCampo,
  EXCEL_PT_BR,
  ICC_MINIMO,
  MINIMO_PARES,
  paraCsv,
  repetibilidadeDaDupla,
  repetibilidadeDoEstudo,
  type ObservacaoDeSessao,
  type ResultadoIcc,
} from '@equiview/domain';
import { useState } from 'react';
import { SemCadastro } from '../components/SemCadastro.js';
import { Abas, Aguardar, Botao, BotaoLink, CabecalhoPagina, classeCampo, classeTd, classeTh, EstadoVazio, Etiqueta, Painel, Tabela } from '../components/ui/index.js';
import { formatarData, hojeIso, traitPorCodigo } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import { baixarArquivo } from '../dados/exportar.js';
import type { Observacao } from '../dados/fonte.js';

const LADO = { E: ' · esquerdo', D: ' · direito', NA: '' } as const;
const INTERPRETACAO: Record<ResultadoIcc['interpretacao'], string> = {
  ruim: 'ruim',
  razoavel: 'razoável',
  boa: 'boa',
  excelente: 'excelente',
};

const nomeDaMedida = (code: string, lado: 'E' | 'D' | 'NA') => `${traitPorCodigo.get(code)?.label ?? code}${LADO[lado]}`;
const icc = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const num = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Observacoes de campo, no formato do dominio. "Nao se aplica" nao entra no ICC. */
const paraSessoes = (obs: readonly Observacao[]): ObservacaoDeSessao[] =>
  obs
    .filter((o) => o.metodo === 'campo')
    .map((o) => ({
      ficha: o.assessment_id,
      trait: o.trait_code,
      lado: o.side,
      avaliador: o.avaliador,
      rodada: o.rodada,
      valor: o.not_applicable ? null : o.value_num,
    }));

function Veredito({ entra }: { entra: boolean | null }) {
  if (entra == null) return <span className="text-apagado">—</span>;
  return entra ? <Etiqueta tom="ok">Entra</Etiqueta> : <Etiqueta tom="aviso">Não entra</Etiqueta>;
}

/**
 * Repetibilidade das medidas (Fatia 6): quais medidas o metodo mede de forma
 * confiavel. Dupla medicao da rotina (ICC(1,1) e Bland-Altman) e o estudo da
 * Fase 0 (ICC(2,1) entre avaliadores e dentro do mesmo avaliador).
 */
export function Repetibilidade() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const [aba, setAba] = useState<'dupla' | 'estudo' | 'foto'>('dupla');
  const [estudoId, setEstudoId] = useState('');
  const consulta = useConsulta(async (f) => {
    const [visitas, avaliacoes] = await Promise.all([f.listarVisitas(), f.avaliacoes()]);
    const daVisita = new Set(visitas.map((v) => v.id));
    const fichas = avaliacoes.filter((a) => daVisita.has(a.visit_id));
    return { visitas, fichas, observacoes: await f.observacoes(fichas.map((a) => a.id)) };
  }, []);

  if (!equipe && !fonte.ficticia) return <SemCadastro demo={false} />;

  return (
    <Aguardar consulta={consulta}>
      {({ visitas, fichas, observacoes }) => {
        const rotina = new Set(visitas.filter((v) => v.purpose === 'rotina').map((v) => v.id));
        const estudos = visitas.filter((v) => v.purpose === 'estudo_repetibilidade');
        const estudo = estudos.find((v) => v.id === estudoId) ?? estudos[0];
        const fichasDe = (pred: (visitId: string) => boolean) => new Set(fichas.filter((a) => pred(a.visit_id)).map((a) => a.id));
        const daRotina = fichasDe((v) => rotina.has(v));
        const doEstudo = fichasDe((v) => v === estudo?.id);

        const dupla = repetibilidadeDaDupla(paraSessoes(observacoes.filter((o) => daRotina.has(o.assessment_id))));
        const linhasEstudo = estudo ? repetibilidadeDoEstudo(paraSessoes(observacoes.filter((o) => doEstudo.has(o.assessment_id)))) : [];
        const fotoCampo = concordanciaFotoCampo(
          observacoes.map((o) => ({
            ficha: o.assessment_id,
            trait: o.trait_code,
            lado: o.side,
            avaliador: o.avaliador,
            rodada: o.rodada,
            metodo: o.metodo,
            valor: o.not_applicable ? null : o.value_num,
          })),
        );

        const exportar = () => {
          const csv =
            aba === 'foto'
              ? paraCsv(
                  [
                    { chave: 'medida', titulo: 'angulo' },
                    { chave: 'pares', titulo: 'pares' },
                    { chave: 'vies', titulo: 'vies_foto_menos_campo' },
                    { chave: 'li', titulo: 'limite_inferior' },
                    { chave: 'ls', titulo: 'limite_superior' },
                  ],
                  fotoCampo.map((l) => ({
                    medida: l.trait,
                    pares: l.pares,
                    vies: l.blandAltman?.vies ?? null,
                    li: l.blandAltman?.limiteInferior ?? null,
                    ls: l.blandAltman?.limiteSuperior ?? null,
                  })),
                  EXCEL_PT_BR,
                )
              : aba === 'dupla'
              ? paraCsv(
                  [
                    { chave: 'medida', titulo: 'medida' },
                    { chave: 'lado', titulo: 'lado' },
                    { chave: 'pares', titulo: 'pares' },
                    { chave: 'icc', titulo: 'icc_1_1' },
                    { chave: 'vies', titulo: 'vies' },
                    { chave: 'li', titulo: 'limite_inferior' },
                    { chave: 'ls', titulo: 'limite_superior' },
                    { chave: 'entra', titulo: 'entra_no_protocolo' },
                  ],
                  dupla.map((l) => ({
                    medida: l.trait,
                    lado: l.lado,
                    pares: l.pares,
                    icc: l.icc?.icc ?? null,
                    vies: l.blandAltman?.vies ?? null,
                    li: l.blandAltman?.limiteInferior ?? null,
                    ls: l.blandAltman?.limiteSuperior ?? null,
                    entra: l.entra,
                  })),
                  EXCEL_PT_BR,
                )
              : paraCsv(
                  [
                    { chave: 'medida', titulo: 'medida' },
                    { chave: 'lado', titulo: 'lado' },
                    { chave: 'entre', titulo: 'icc_entre_avaliadores' },
                    { chave: 'fichas', titulo: 'fichas' },
                    { chave: 'avaliadores', titulo: 'avaliadores' },
                    { chave: 'dentro', titulo: 'icc_dentro_do_avaliador' },
                    { chave: 'entra', titulo: 'entra_no_protocolo' },
                  ],
                  linhasEstudo.map((l) => ({
                    medida: l.trait,
                    lado: l.lado,
                    entre: l.entre?.icc ?? null,
                    fichas: l.entre?.sujeitos ?? null,
                    avaliadores: l.entre?.avaliadores ?? null,
                    dentro: l.dentro?.icc ?? null,
                    entra: l.entra,
                  })),
                  EXCEL_PT_BR,
                );
          baixarArquivo(`equiview-repetibilidade-${aba}-${hojeIso()}.csv`, csv);
        };

        const avaliadas = aba === 'foto' ? [] : (aba === 'dupla' ? dupla : linhasEstudo).filter((l) => l.entra != null);
        const passam = avaliadas.filter((l) => l.entra).length;

        return (
          <div className="space-y-6">
            <CabecalhoPagina
              titulo="Repetibilidade das medidas"
              meta={<span>Quais medidas o método mede de forma confiável (ICC ≥ {icc(ICC_MINIMO)}).</span>}
              acoes={
                <div className="flex flex-wrap gap-2 print:hidden">
                  <BotaoLink to="/dados">Consulta e exportação</BotaoLink>
                  <Botao onClick={exportar}>Exportar relatório</Botao>
                  <Botao onClick={() => window.print()}>Imprimir</Botao>
                </div>
              }
            />
            <Abas
              rotulo="Análise"
              valor={aba}
              onMudar={setAba}
              abas={[
                { valor: 'dupla', rotulo: 'Dupla medição (rotina)', contagem: dupla.length },
                { valor: 'estudo', rotulo: 'Estudo (Fase 0)', contagem: estudos.length },
                { valor: 'foto', rotulo: 'Foto × campo (Fase 1)', contagem: fotoCampo.length },
              ]}
            />

            {avaliadas.length > 0 && (
              <p className="text-sm">
                <span className="font-semibold">{passam}</span> de {avaliadas.length} medida(s) com dado suficiente passam de{' '}
                {icc(ICC_MINIMO)}.
              </p>
            )}

            {aba === 'foto' ? (
              fotoCampo.length === 0 ? (
                <EstadoVazio titulo="Nenhum ângulo medido por foto ainda">
                  Na página de uma foto de perfil, "Medir ângulos na foto" marca os pontos e grava os ângulos. Eles aparecem aqui
                  comparados com o goniômetro do campo.
                </EstadoVazio>
              ) : (
                <Painel titulo="Fotogrametria × goniômetro" corpo="p-0">
                  <Tabela rotulo="Foto contra campo" empilhar>
                    <thead>
                      <tr>
                        <th scope="col" className={classeTh}>Ângulo</th>
                        <th scope="col" className={`${classeTh} text-right`}>Pares</th>
                        <th scope="col" className={`${classeTh} text-right`}>Viés (foto − campo)</th>
                        <th scope="col" className={`${classeTh} text-right`}>Limites (95%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fotoCampo.map((l) => (
                        <tr key={l.trait}>
                          <td className={classeTd}>{nomeDaMedida(l.trait, 'NA')}</td>
                          <td data-rotulo="Pares" className={`${classeTd} numero text-right`}>{l.pares}</td>
                          <td data-rotulo="Viés" className={`${classeTd} numero text-right`}>
                            {l.blandAltman ? `${num(l.blandAltman.vies)}°` : `menos de ${MINIMO_PARES} pares`}
                          </td>
                          <td data-rotulo="Limites" className={`${classeTd} numero text-right`}>
                            {l.blandAltman ? `${num(l.blandAltman.limiteInferior)}° a ${num(l.blandAltman.limiteSuperior)}°` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Tabela>
                  <p className="border-t border-borda px-4 py-3 text-xs text-apagado">
                    Bland-Altman: o viés é a diferença média entre a foto e o goniômetro; 95% das diferenças caem entre os
                    limites. A foto substitui o campo num ângulo quando o viés é pequeno e os limites cabem na tolerância do
                    protocolo — decisão da coordenação científica.
                  </p>
                </Painel>
              )
            ) : aba === 'dupla' ? (
              dupla.length === 0 ? (
                <EstadoVazio titulo="Nenhuma dupla medição ainda">
                  A dupla medição aparece aqui quando as fichas sorteadas tiverem a segunda medição.
                </EstadoVazio>
              ) : (
                <Painel titulo="1ª × 2ª medição nas fichas sorteadas" corpo="p-0">
                  <Tabela rotulo="Dupla medição" empilhar>
                    <thead>
                      <tr>
                        <th scope="col" className={classeTh}>Medida</th>
                        <th scope="col" className={`${classeTh} text-right`}>Pares</th>
                        <th scope="col" className={`${classeTh} text-right`}>ICC(1,1)</th>
                        <th scope="col" className={`${classeTh} hidden text-right sm:table-cell`}>Viés</th>
                        <th scope="col" className={`${classeTh} hidden text-right md:table-cell`}>Limites (95%)</th>
                        <th scope="col" className={classeTh}>Protocolo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dupla.map((l) => (
                        <tr key={`${l.trait}-${l.lado}`}>
                          <td className={classeTd}>{nomeDaMedida(l.trait, l.lado)}</td>
                          <td data-rotulo="Pares" className={`${classeTd} numero text-right`}>{l.pares}</td>
                          <td data-rotulo="ICC" className={`${classeTd} numero text-right`}>
                            {l.icc ? `${icc(l.icc.icc)} · ${INTERPRETACAO[l.icc.interpretacao]}` : `menos de ${MINIMO_PARES} pares`}
                          </td>
                          <td data-rotulo="Viés" className={`${classeTd} numero hidden text-right sm:table-cell`}>
                            {l.blandAltman ? num(l.blandAltman.vies) : '—'}
                          </td>
                          <td className={`${classeTd} numero hidden text-right md:table-cell`}>
                            {l.blandAltman ? `${num(l.blandAltman.limiteInferior)} a ${num(l.blandAltman.limiteSuperior)}` : '—'}
                          </td>
                          <td data-acao className={classeTd}>
                            <Veredito entra={l.entra} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Tabela>
                  <p className="border-t border-borda px-4 py-3 text-xs text-apagado">
                    ICC(1,1): o par de avaliadores muda de ficha para ficha. Viés: média da 2ª menos a 1ª; limites de
                    concordância de Bland-Altman. As medidas menos repetíveis aparecem primeiro.
                  </p>
                </Painel>
              )
            ) : !estudo ? (
              <EstadoVazio titulo="Nenhuma visita de estudo">
                Planeje uma visita do tipo "Estudo de repetibilidade (Fase 0)": vários avaliadores medem os mesmos animais,
                duas vezes cada.
              </EstadoVazio>
            ) : (
              <Painel
                titulo={`Estudo de ${formatarData(estudo.visit_date)}`}
                corpo="p-0"
                acoes={
                  estudos.length > 1 && (
                    <select value={estudo.id} onChange={(e) => setEstudoId(e.target.value)} className={`${classeCampo()} w-auto`}>
                      {estudos.map((v) => (
                        <option key={v.id} value={v.id}>
                          Nº {v.visit_number} · {formatarData(v.visit_date)}
                        </option>
                      ))}
                    </select>
                  )
                }
              >
                <Tabela rotulo="Estudo de repetibilidade" empilhar>
                  <thead>
                    <tr>
                      <th scope="col" className={classeTh}>Medida</th>
                      <th scope="col" className={`${classeTh} text-right`}>Entre avaliadores</th>
                      <th scope="col" className={`${classeTh} text-right`}>Mesmo avaliador</th>
                      <th scope="col" className={classeTh}>Protocolo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhasEstudo.map((l) => (
                      <tr key={`${l.trait}-${l.lado}`}>
                        <td className={classeTd}>{nomeDaMedida(l.trait, l.lado)}</td>
                        <td data-rotulo="Entre avaliadores" className={`${classeTd} numero text-right`}>
                          {l.entre ? `${icc(l.entre.icc)} · ${l.entre.sujeitos} animais × ${l.entre.avaliadores}` : '—'}
                        </td>
                        <td data-rotulo="Mesmo avaliador" className={`${classeTd} numero text-right`}>
                          {l.dentro ? `${icc(l.dentro.icc)} · ${l.dentro.avaliadores} aval.` : '—'}
                        </td>
                        <td data-acao className={classeTd}>
                          <Veredito entra={l.entra} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Tabela>
                <p className="border-t border-borda px-4 py-3 text-xs text-apagado">
                  Entre avaliadores: ICC(2,1) na 1ª rodada, só com os animais que todos mediram. Mesmo avaliador: ICC(2,1) da 1ª
                  contra a 2ª rodada, média entre os avaliadores. A medida entra no protocolo com os dois ≥ {icc(ICC_MINIMO)}.
                </p>
              </Painel>
            )}
          </div>
        );
      }}
    </Aguardar>
  );
}
